import { Component, HostBinding, Input } from '@angular/core';

import { Store, createSelector } from '@ngrx/store';

import { environment } from '../../../../environments/environment';
import { Location } from '../../../../generated/graphql.generated';
import { FreyaCommonModule } from '../../../freya-common/freya-common.module';
import { GoogleHelperService } from '../../../services/google-helper.service';
import { SharedModule } from '../../../shared/shared.module';
import { JobLocationsOverviewActions } from '../overview-locations/overview-locations.actions';
import { selectCanCalculateDistances, selectOrderedLocations } from '../overview-locations/overview-locations.selectors';
import { TravelTimeComponent } from '../travel-time/travel-time.component';

const MAPS_BASE = 'https://www.google.com/maps/embed/v1/';

function encodeDistanceLocation(location: any): string {
  if (!location) { return; }
  const address = location.addressLineOne ? encodeURIComponent(location.addressLineOne) : '';
  const city = location.city ? encodeURIComponent(location.city) : '';
  const country = location.country ? encodeURIComponent(location.country) : '';
  return [address, city, country].join(',');
}

function getDirectionsUrl(key: string, origin: string, destination: string, waypoints?: string[]): string {
  let params = `origin=${origin}&destination=${destination}`;
  if (waypoints) {
    const waypointsParam = `&waypoints=${waypoints.join('|')}`;
    params = params + waypointsParam;
  };
  return `${MAPS_BASE}directions?key=${key}&${params}`;
}

function getPlaceUrl(key: string, place: string): string {
  const url = new URL(MAPS_BASE);
  url.pathname += 'place';
  url.searchParams.set('key', key);
  url.searchParams.set('q', `${place}`);

  return url.toString();
}

function getViewUrl(key: string, lat: number, long: number, zoom: number): string {
  const url = new URL(MAPS_BASE);
  url.pathname += 'view';
  url.searchParams.set('key', key);
  url.searchParams.set('center', `${lat},${long}`);
  url.searchParams.set('zoom', `${zoom}`);

  return url.toString();
}

function getRouteUrl(key: string, route: Partial<Location>[]): string {

  if (route.length === 0) {
    return getViewUrl(key, environment.defaultMapCoordinates[0], environment.defaultMapCoordinates[1], 4);
  }

  if (route.length === 1) {
    return getPlaceUrl(key, encodeDistanceLocation(route[0]));
  }

  const origin = encodeDistanceLocation(route[0]);
  const destination = encodeDistanceLocation(route[route.length - 1]);

  if (route.length === 2) {
    return getDirectionsUrl(key, origin, destination);
  }

  const waypoints = route
    .slice(1, -1)
    .map((location) => encodeDistanceLocation(location));

  return getDirectionsUrl(key, origin, destination, waypoints);
}

const selectRoute = createSelector(
  selectOrderedLocations,
  (orderedLocations) => {

    const route: Partial<Location>[] = [];

    for (const location of orderedLocations) {
      if (location.location) {
        route.push(location.location);
      } else {
        break;
      }
    }

    return route;
  }
);

@Component({
  selector: 'app-travel-map',
  standalone: true,
  imports: [
    FreyaCommonModule,
    SharedModule,
    TravelTimeComponent,
  ],
  templateUrl: './travel-map.component.html',
  styleUrls: ['./travel-map.component.scss']
})
export class TravelMapComponent {

  @HostBinding('style.--map-height') @Input() mapHeight = '45vh';

  mapLoading = true;

  constructor(
    private googleHelper: GoogleHelperService,
    public store: Store,
  ) { }

  public viewModel$ = this.store.select(createSelector({
    url: createSelector(selectRoute, (route) => getRouteUrl(this.googleHelper.getKey(), route)),
    showOpenRouteLink: createSelector(selectRoute, (route) => route.length > 1),
    canCalculateDistances: selectCanCalculateDistances,
  }));

  public actions = JobLocationsOverviewActions;

}
